import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import moment from "moment";
import { Box } from "@mui/material";
import { MyLeaveCalendar, MyLeaveOverlapsTables } from "my-leave/components";
import {
  allLeaveRequestsWithCoverage,
  allLeaveRequestsWithCoverageIsMobile,
  allLeaveRequestsDailyTable,
} from "my-leave/configs/table-defs";
import { OcCard } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import { useOcButtonGroup, useOcTableState, useOcIsMobile } from "oc/hooks";
import { getFilterValues } from "oc/utils";
import parsePipedName from "oc/utils/get-parse-pipedName";
import getSortedList from "oc/utils/get-sorted-list";

type MyLeaveAllLeavesWithCoverageProps = {
  currentDate: Date | string;
  onCurrentDateChange: (date: Date | string) => void;
  monthFiltering: boolean;
  cardTitle: string;
  viewedByAdmin: boolean;
  showRemoteWorks?: boolean;
  leavesData: any[];
  loading: boolean;
  eventsByDays: any[];
  role?: string;
};

export const MyLeaveAllLeavesWithCoverage = ({
  currentDate,
  onCurrentDateChange,
  monthFiltering,
  cardTitle,
  viewedByAdmin,
  showRemoteWorks = true,
  leavesData,
  loading,
  eventsByDays,
  role: roleProp,
}: MyLeaveAllLeavesWithCoverageProps) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();

  let role = "";

  if (roleProp) {
    role = roleProp;
  } else if (viewedByAdmin) {
    role = "viewer";
  } else if (!viewedByAdmin) {
    role = "examiner";
  }

  const buttonsDef = ["OC_CALENDAR", "OC_TABLE"];
  const { OcButtonGroup, selectedButton, handleButtonChange } =
    useOcButtonGroup({
      id: "AllLeavesWithCoverage",
      buttonsDef,
    });

  const [selectedDate, setSelectedDate] = useState(false);

  const allLeavesId = "allLeaveRequestTable";
  const allLeavesByDayId = "allLeaveRequestByDayTable";

  const [tableAllData, setTableAllData] = useOcTableState(allLeavesId, {
    ...initialTableData,
    pageSize: 5,
  });
  const [tableAllByDayData, setTableAllByDayData] = useOcTableState(
    allLeavesByDayId,
    {
      ...initialTableData,
      pageSize: 5,
    },
  );
  const [toolbarButton, setToolbarButton] = useState("basicTable");
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const tableId =
    toolbarButton === "basicTable" ? allLeavesId : allLeavesByDayId;
  const tableData =
    toolbarButton === "basicTable" ? tableAllData : tableAllByDayData;
  const setTableData =
    toolbarButton === "basicTable" ? setTableAllData : setTableAllByDayData;

  const eventsData =
    toolbarButton === "basicTable" || selectedButton === "OC_CALENDAR"
      ? leavesData
      : eventsByDays;

  let counter = 0;
  let parsedLeaveData =
    eventsData?.map((item) => {
      let newItem = _.cloneDeep(item);
      if (newItem.title.includes("|")) {
        newItem.title = parsePipedName(newItem.title, i18n);
      }
      newItem.sortName = item.sortName || "";
      newItem.typeName = t(`${item.type}`);
      newItem.type = t(item.leaveType);
      newItem.period =
        new Date(item.start).toLocaleDateString() +
        " - " +
        new Date(item.end).toLocaleDateString();

      // Convert string dates to Date objects for BigCalendar
      // Backend returns dates as 'YYYY-MM-DD' strings to avoid timezone issues
      // Store original dates for tooltip/display purposes
      newItem.originalStart = item.start;
      newItem.originalEnd = item.end;
      // BigCalendar requires Date objects in LOCAL timezone and treats end dates as exclusive,
      // so we add 1 day and use startOf('day') to ensure proper day boundaries
      newItem.start = moment(item.start).startOf('day').toDate();
      newItem.end = moment(item.end).add(1, 'day').startOf('day').toDate();

      newItem.counter = counter++;
      if (newItem._id.startsWith("jd-")) {
        newItem.state =
          newItem.effectiveWorkDays > 0 ? t("recorded") : t("cancelled");
      }
      return newItem;
    }) || [];

  parsedLeaveData.forEach((item) => {
    if (item.date) {
      item.sortableDate = new Date(item.date).getTime();
    }
  });

  parsedLeaveData.sort((a, b) => {
    if (tableData.sortDirection === "asc") {
      return a.sortableDate - b.sortableDate;
    } else {
      return b.sortableDate - a.sortableDate;
    }
  });

  const currentTableDef = toolbarButton === "basicTable" || selectedButton === "OC_CALENDAR"
    ? (isMobile ? allLeaveRequestsWithCoverageIsMobile : allLeaveRequestsWithCoverage)
    : allLeaveRequestsDailyTable;

  const sortColumn = (currentTableDef?.columns as any[])?.find(
    (col: any) => col.id === tableData?.sortField
  );

  const actualSortField = sortColumn?.sortAccessor || tableData?.sortField;

  parsedLeaveData.sort(
    getSortedList(
      tableData?.sortDirection === "asc"
        ? actualSortField
        : `-${actualSortField}`,
    ),
  );

  if (selectedDate) {
    parsedLeaveData = parsedLeaveData.filter(
      (item) => new Date(item?.date) >= new Date(currentDate),
    );
  }

  let filteredLeavesData = [];
  let filteredParsedLeavesData = [];

  if (!showRemoteWorks) {
    filteredLeavesData = eventsByDays?.filter(
      (item) =>
        item.leaveType === "leave" ||
        (item.type !== "O" && item.type !== "J" && item.type !== "I"),
    );
    filteredParsedLeavesData = parsedLeaveData.filter(
      (item) => item.leaveType === "leave",
    );
  }

  let events = showRemoteWorks ? parsedLeaveData : filteredLeavesData;

  const searchText = tableData?.searchText;
  const searchField = tableData?.searchField;

  let rows;

  if (showRemoteWorks) {
    const filteredResult = events.filter(
      (item) =>
        searchText && new RegExp(searchText, "i").test(item?.[searchField]),
    );

    rows = searchText.length === 0 ? events : filteredResult;
  } else {
    rows = filteredParsedLeavesData;
  }

  if (searchField) {
    rows = rows.filter((item) =>
      item?.title.toLowerCase().includes(searchText.toLowerCase()),
    );
  }

  // Apply filter based on selected filter values
  if (tableData?.filterSelected && tableData.filterSelected.length > 0) {
    rows = rows.filter((item) => {
      const key = item[tableData?.filterField];
      return tableData?.filterSelected.includes(key);
    });
  }

  const startOfMonth = moment(currentDate).startOf("month").toDate();
  const endOfMonth = moment(currentDate).endOf("month").toDate();
  const tableLeavesData = rows?.filter(
    (item) =>
      (moment(item.start).format("YYYY-MM-DD") >=
        moment(startOfMonth).format("YYYY-MM-DD") &&
        moment(item.start).format("YYYY-MM-DD") <=
        moment(endOfMonth).format("YYYY-MM-DD")) ||
      (moment(item.end).format("YYYY-MM-DD") >=
        moment(startOfMonth).format("YYYY-MM-DD") &&
        moment(item.end).format("YYYY-MM-DD") <=
        moment(endOfMonth).format("YYYY-MM-DD")),
  );

  // Calculate total AFTER all filtering
  let total = tableLeavesData.length;

  let data = {
    rows: tableLeavesData,
    total: total,
    page: tableData?.page,
    pageSize: tableData?.pageSize,
    searchField: tableData?.searchField,
    searchText: tableData?.searchText,
  };

  // Calendar should show filtered data based on showRemoteWorks
  const calendarEvents = showRemoteWorks
    ? parsedLeaveData
    : parsedLeaveData.filter((item) => item.leaveType === "leave");

  const originalonSelectRow = (event: any) => {
    const { _id, contract } = event.row ?? event;
    history.push(`/my-leave/user-request/MyLeave/${role}/${contract}/${_id}`);
  };

  const onSelectRow = (o: { _id: string; contract: string }) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? null : o?._id);
    } else {
      originalonSelectRow(o);
    }
  };

  const handleTable = () => {
    setToolbarButton("basicTable");
  };

  const handleViewDayClick = () => {
    setToolbarButton("dailyTable");
  };

  const handleAgendaViewClick = () => {
    setToolbarButton("dailyTable");
    handleButtonChange(1);
    setSelectedDate(true);
  };

  useEffect(() => {
    if (selectedButton === "OC_CALENDAR") {
      handleButtonChange(0);
      setSelectedDate(false);
    } else {
      handleButtonChange(1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedButton]);

  useEffect(() => {
    if (isMobile) {
      handleButtonChange(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // FIX: filterValues ​​initialization and update when filterField or data changes
  React.useEffect(() => {
    const field = tableData?.filterField;
    if (field && parsedLeaveData.length > 0) {
      const filterValues = getFilterValues(field, parsedLeaveData);
      if (filterValues.length > 0) {
        // Always update filterValues when filterField changes
        setTableData({ filterValues });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData?.filterField, parsedLeaveData.length]);

  // Clear filter when month changes
  React.useEffect(() => {
    if (tableData?.filterField || tableData?.filterSelected?.length > 0) {
      setTableData({
        filterField: "",
        filterSelected: [],
        filterValues: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  return (
    <OcCard label={cardTitle} loading={loading}>
      <Box id="my-leave-all-leaves-with-coverage-card">
        {!isMobile && (
          <Box
            id="button-group"
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            padding="18px 0px"
          >
            <OcButtonGroup />
          </Box>
        )}
        <Box paddingTop="20px">
          {selectedButton === "OC_CALENDAR" ? (
            <MyLeaveCalendar
              events={calendarEvents}
              height={900}
              currentDate={currentDate}
              onCurrentDateChange={onCurrentDateChange}
              monthFiltering={monthFiltering}
              onEventSelected={onSelectRow}
              setToolbarButton={setToolbarButton}
              // @ts-ignore
              handleButtonChange={handleButtonChange}
              loading={loading}
              // @ts-ignore
              handleAgendaViewClick={handleAgendaViewClick}
            />
          ) : (
            <MyLeaveOverlapsTables
              currentDate={currentDate}
              onCurrentDateChange={onCurrentDateChange}
              loading={loading}
              data={data}
              tableId={tableId}
              onSelectRow={onSelectRow}
              tableData={tableData}
              setTableData={setTableData}
              handleTable={handleTable}
              handleViewDayClick={handleViewDayClick}
              toolbarButton={toolbarButton}
              isMobile={isMobile}
              selectedRowId={selectedRowId ?? ""}
              handleMoreButton={originalonSelectRow}
            />
          )}
        </Box>
      </Box>
    </OcCard>
  );
};

export default MyLeaveAllLeavesWithCoverage;
